// ============================================
// FILE: src/utils/fashionHelpers.js
// ============================================
//
// Decides whether a category or product should open the fashion screens
// (portrait grid, colour/size selection, media gallery) or the existing
// grocery ones.
//
// The authoritative signal is `categories.template`, set in the admin. The
// name hints below are a safety net so the fashion catalogue works the day it
// is seeded, before anyone has flipped the flag — and so a category the admin
// forgets still lands on a sensible screen.

const FASHION_TEMPLATE = 'fashion';

const FASHION_NAME_HINTS = [
  'fashion', 'clothing', 'apparel', 'footwear', 'shoes', 'sandal', 'sneaker',
  't-shirt', 'tshirt', 'tee', 'shirt', 'dress', 'kurta', 'kurti', 'jeans',
  'trouser', 'ethnic', 'saree', 'topwear', 'top wear', 'bottomwear',
  'accessories', 'jacket', 'sweater', 'hoodie', 'skirt',
];

const matchesNameHint = (value) => {
  if (!value || typeof value !== 'string') return false;
  const name = value.toLowerCase();
  return FASHION_NAME_HINTS.some((hint) => name.includes(hint));
};

/** True when this category should render with the fashion screens. */
export const isFashionCategory = (category) => {
  if (!category) return false;
  if (category.template) return category.template === FASHION_TEMPLATE;

  return matchesNameHint(category.name) || matchesNameHint(category.slug);
};

/**
 * True when this product should open the fashion detail screen.
 * A product inherits its category's template; the detail payload carries a
 * top-level `template` for exactly this reason.
 */
export const isFashionProduct = (product) => {
  if (!product) return false;
  if (product.template) return product.template === FASHION_TEMPLATE;

  // `category` is a display-name string on card payloads and an object on the
  // detail payload — handle both.
  if (product.category && typeof product.category === 'object') {
    return isFashionCategory(product.category);
  }

  return matchesNameHint(product.category);
};

/** 'fashion' | 'grocery' for either a category or a product. */
export const getFashionTemplate = (categoryOrProduct) => {
  const isFashion = categoryOrProduct?.products !== undefined || categoryOrProduct?.slug
    ? isFashionCategory(categoryOrProduct) || isFashionProduct(categoryOrProduct)
    : isFashionProduct(categoryOrProduct);

  return isFashion ? FASHION_TEMPLATE : 'grocery';
};
